// Helper to aggregate donut chart data based on metric type and orders data

type Order = { mission_advancement_id: number; count: number };
type rating = 'A' | 'B' | 'C' | 'D' | 'E' | 'S' | 99;

interface DonutAggregationResult {
  donutDataConsultant: Record<string, number>;
  donutDataDM: Record<string, number>;
  donutDataClient: Record<string, number>;
}

interface RatingDetail {
  rating: rating;
  count: number;
}

interface GraphDataOrders {
  consultant_submission?: Order[];
  dm_validation?: Order[];
  client_acceptance?: Order[];
  rating_details?: RatingDetail[];
}

// Status mapper function
type Context = 'consultant' | 'dm' | 'client';

const STATUS_MAP: Record<number, Partial<Record<Context, string>>> = {
  1: {
    dm: 'TO BE VALIDATED', // 'TO VALIDATE BY DM',
    consultant: 'SUBMITTED',
    client: 'PENDING DELIVERY', // 'PENDING DM VALIDATION',
  },
  2: {
    dm: 'PENDING SUBMISSION', // 'DM REFUSE',
    consultant: 'NOT SUBMITTED',
    client: 'PENDING DELIVERY', // 'PENDING DM VALIDATION',
  },
  3: {
    dm: 'VALIDATION COMPLETE', // 'DM VALIDATED',
    consultant: 'SUBMITTED',
    client: 'TO BE ACCEPTED', // 'WAITING CLIENT ACCEPTANCE',
  },
  4: {
    dm: 'VALIDATION COMPLETE', // 'DM VALIDATED WITH RESERVATION',
    consultant: 'SUBMITTED',
    client: 'TO BE ACCEPTED', // 'WAITING CLIENT ACCEPTANCE',
  },
  5: {
    dm: 'TO BE VALIDATED', // 'TO VALIDATE BY DM',
    consultant: 'SUBMITTED',
    client: 'ACCEPTANCE COMPLETE', //  'CLIENT REFUSE',
  },
  6: {
    dm: 'VALIDATION COMPLETE', //  'DM VALIDATED',
    consultant: 'SUBMITTED',
    client: 'ACCEPTANCE COMPLETE', // 'CLIENT ACCEPTED WITH RESERVATION',
  },
  7: {
    dm: 'VALIDATION COMPLETE', // 'DM VALIDATED',
    consultant: 'SUBMITTED',
    client: 'ACCEPTANCE COMPLETE', // 'CLIENT ACCEPTED',
  },
  99: {
    dm: 'PENDING SUBMISSION',
    consultant: 'NOT SUBMITTED',
    client: 'PENDING DELIVERY', // 'PENDING DM VALIDATION',
  },
};

const DEFAULT_STATUS: Record<number, string> = {
  2: 'DM REFUSE',
  3: 'DM VALIDATED',
  4: 'DM VALIDATED WITH RESERVATION',
  5: 'CLIENT REFUSE',
  6: 'CLIENT ACCEPTED WITH RESERVATION',
  7: 'CLIENT ACCEPTED',
  99: 'NOT SUBMITTED',
};

export function getStatus(id: number, context?: Context): string {
  if (context && STATUS_MAP[id]?.[context]) {
    return STATUS_MAP[id]![context]!;
  }
  return DEFAULT_STATUS[id] || '';
}

// Merge consultant data (combine ID 99 and 2)
function mergeConsultantData(list: Order[]): Order[] {
  if (!Array.isArray(list)) return [];

  let arr = [...list];
  const obj99 = arr.find((o) => o.mission_advancement_id === 99);
  const obj2 = arr.find((o) => o.mission_advancement_id === 2);

  if (obj99 && obj2) {
    const index99 = arr.findIndex((o) => o.mission_advancement_id === 99);
    if (index99 !== -1) {
      arr[index99] = { ...arr[index99], count: arr[index99].count + obj2.count };
    }
    arr = arr.filter((o) => o.mission_advancement_id !== 2);
  }
  return arr;
}

/**
 * Aggregate donut data for metric 1 (Workflow)
 */
function aggregateMetric1(orders: GraphDataOrders): DonutAggregationResult {
  const consultant = { SUBMITTED: 0, 'NOT SUBMITTED': 0 };
  const dm = {
    // 1.	To be validated by DM: 0,
    // 2.	Validation complete :0
    // 3.	Pending Submission: 0
    'TO BE VALIDATED': 0,
    'VALIDATION COMPLETE': 0,
    'PENDING SUBMISSION': 0,

    // 'DM REFUSE': 0,
    // 'TO VALIDATE BY DM': 0,
    // 'DM PENDING SUBMISSION': 0,
    // 'DM VALIDATED': 0,
    // 'DM VALIDATED WITH RESERVATION': 0,
  };
  const client = {
    'TO BE ACCEPTED': 0,
    'ACCEPTANCE COMPLETE': 0,
    'PENDING DELIVERY': 0,
    // 'CLIENT REFUSE': 0,
    // 'WAITING CLIENT ACCEPTANCE': 0,
    // 'CLIENT ACCEPTED': 0,
    // 'PENDING DM VALIDATION': 0,
    // 'CLIENT ACCEPTED WITH RESERVATION': 0,

    //     1.	TO BE ACCEPTED
    // 2.	ACCEPTANCE COMPLETE (For ACL: include Accepted, Refused, Accepted with reserve)
    // 3.	PENDING DELIVERY (for ACL: To be submitted + To be Validated)
  };

  // Merge consultant data first
  const mergedConsultant = mergeConsultantData(orders.consultant_submission || []);

  // Process consultant data
  mergedConsultant.forEach((order) => {
    const status = getStatus(order.mission_advancement_id, 'consultant');
    if (status in consultant) {
      consultant[status as keyof typeof consultant] += order.count;
    }
  });

  // Process DM and Client data
  (['dm_validation', 'client_acceptance'] as const).forEach((category) => {
    const categoryData = orders[category];
    if (Array.isArray(categoryData)) {
      categoryData.forEach((order) => {
        const context = category === 'dm_validation' ? 'dm' : 'client';
        const status = getStatus(order.mission_advancement_id, context);

        if (category === 'dm_validation' && status in dm) {
          dm[status as keyof typeof dm] += order.count;
        } else if (category === 'client_acceptance' && status in client) {
          client[status as keyof typeof client] += order.count;
        }
      });
    }
  });

  return {
    donutDataConsultant: consultant,
    donutDataDM: dm,
    donutDataClient: client,
  };
}

/**
 * Aggregate donut data for metric 2 (Acceptance)
 * Maps mission_advancement_id to acceptance states
 */
function aggregateMetric2(orders: GraphDataOrders): DonutAggregationResult {
  // For metric 2, we adapt the structure to acceptance states

  const client = {
    ACCEPTED: 0,
    'ACCEPTED WITH RESERVATION': 0,
    REFUSED: 0,
    'TO BE DONE': 0,
  };

  // Process client acceptance data (primary data for metric 2)
  const clientData = orders.client_acceptance || [];
  clientData.forEach((order) => {
    switch (order.mission_advancement_id) {
      case 7: // Accepted
        client.ACCEPTED += order.count;
        break;
      case 6: // Accepted with reservation
        client['ACCEPTED WITH RESERVATION'] += order.count;
        break;
      case 5: // Rejected
        client.REFUSED += order.count;
        break;
      case 1:
      case 2:
      case 3: // Pending
      case 4:
      case 99:
        client['TO BE DONE'] += order.count;
        break;
      default:
        break;
    }
  });

  return {
    donutDataConsultant: {},
    donutDataDM: {},
    donutDataClient: client,
  };
}

function aggregateMetric3(orders: GraphDataOrders): DonutAggregationResult {
  // For metric 3, aggregate by rating type
  const client: Record<string, number> = {
    A: 0,
    B: 0,
    C: 0,
    D: 0,
    E: 0,
    S: 0,
    // 'Not Rated': 0,
  };
  const clientData = orders.rating_details || [];

  clientData.forEach((order: { rating: rating; count: number }) => {
    // if (order.rating === 99) {
    //   client['Not Rated'] += order.count;
    // } else
    if (['A', 'B', 'C', 'D', 'E', 'S'].includes(order.rating as string)) {
      client[order.rating as string] += order.count;
    }
  });
  // console.log(client);

  return {
    donutDataConsultant: {},
    donutDataDM: {},
    donutDataClient: client,
  };
}

/**
 * Main aggregation function that routes to metric-specific aggregators
 */
export default function aggregateDonutData(orders: GraphDataOrders, metricId = 1): DonutAggregationResult {
  if (metricId === 2) {
    return aggregateMetric2(orders);
  }
  if (metricId === 3) {
    return aggregateMetric3(orders);
  }
  // Default to metric 1
  return aggregateMetric1(orders);
}
